import { gql, useQuery } from "@apollo/client";
import {
  Shipment,
  ShipmentStopType,
  TaskStatus,
} from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";

const OVERRIDE_POD_SHIPMENT_QUERY = gql`
  query OverridePODShipment($courierId: ID!, $shipmentId: ID!) {
    shipmentById(courierId: $courierId, shipmentId: $shipmentId) {
      id
      driverId
      signedByOverride
      deliveredAtOverride
      driver {
        id
      }
      order {
        displayId
      }
      stops {
        id
        stopSequenceNumber
        completedAt
        city
        companyName
        dispatchZone
        streetAddress
        suite
        state
        zipCode
        type
        tasks {
          id
          sequenceNumber
          status
          arrivedAt
          date
        }
      }
    }
  }
`;

export interface OverridePODShipmentTask {
  id: string;
  sequenceNumber: number;
  status: TaskStatus;
  arrivedAt?: number | null;
  date?: string | null;
}

export interface OverridePODShipmentStop {
  id: string;
  type: ShipmentStopType;
  stopSequenceNumber?: number | null;
  completedAt?: number | null;
  city?: string | null;
  companyName?: string | null;
  dispatchZone?: string | null;
  streetAddress?: string | null;
  suite?: string | null;
  state?: string | null;
  zipCode?: string | null;
  tasks?: OverridePODShipmentTask[] | null;
}

export interface OverridePODShipment {
  id: string;
  driverId?: string | null;
  signedByOverride?: string | null;
  deliveredAtOverride?: number | null;
  driver?: { id: string } | null;
  order?: {
    displayId?: number | null;
  } | null;
  stops?: OverridePODShipmentStop[] | null;
}

interface OverridePODShipmentQueryData {
  shipmentById: OverridePODShipment | null;
}

interface OverridePODShipmentQueryVariables {
  courierId: string;
  shipmentId: string;
}

interface UseOverridePODShipmentOptions {
  shipmentId?: string;
  skip?: boolean;
}

export function useOverridePODShipment({
  shipmentId,
  skip = false,
}: UseOverridePODShipmentOptions) {
  const { courierId } = useAuth();

  const { data, loading, error, refetch } = useQuery<
    OverridePODShipmentQueryData,
    OverridePODShipmentQueryVariables
  >(OVERRIDE_POD_SHIPMENT_QUERY, {
    variables: {
      courierId: courierId!,
      shipmentId: shipmentId!,
    },
    skip: skip || !courierId || !shipmentId,
    fetchPolicy: "no-cache",
  });

  return {
    shipment: data?.shipmentById
      ? (data.shipmentById as unknown as Shipment)
      : undefined,
    loading,
    error,
    refetch,
  };
}
